import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Loader2, Lock, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  useDeleteSubAccount,
  usePlanComptable,
  useUpsertSubAccount,
  type UpsertSubAccountInput,
} from '@/hooks/queries/usePlanComptable';
import { useRole } from '@/hooks/useRole';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { PlanComptableRow } from '@/lib/database.types';

interface NodeWithChildren extends PlanComptableRow {
  children: NodeWithChildren[];
}

function buildTree(rows: PlanComptableRow[]): NodeWithChildren[] {
  const byId = new Map<string, NodeWithChildren>();
  rows.forEach((r) => byId.set(r.id, { ...r, children: [] }));
  const roots: NodeWithChildren[] = [];
  for (const node of byId.values()) {
    if (node.parent_id && byId.has(node.parent_id)) {
      const parent = byId.get(node.parent_id);
      if (parent) parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (n: NodeWithChildren): void => {
    n.children.sort((a, b) => a.numero.localeCompare(b.numero, undefined, { numeric: true }));
    n.children.forEach(sortRec);
  };
  roots.sort((a, b) => a.numero.localeCompare(b.numero, undefined, { numeric: true }));
  roots.forEach(sortRec);
  return roots;
}

export function PlanComptablePage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: rows = [], isLoading } = usePlanComptable();
  const { data: entreprises = [] } = useEntreprises();
  const upsert = useUpsertSubAccount();
  const remove = useDeleteSubAccount();

  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<{
    open: boolean;
    parent: PlanComptableRow | null;
    editing: PlanComptableRow | null;
  }>({
    open: false,
    parent: null,
    editing: null,
  });
  const [confirmDelete, setConfirmDelete] = useState<PlanComptableRow | null>(null);

  const tree = useMemo(() => buildTree(rows), [rows]);

  // filter recursively
  const filtered = useMemo(() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();
    const match = (n: NodeWithChildren): NodeWithChildren | null => {
      const childMatches = n.children.map(match).filter((c): c is NodeWithChildren => c !== null);
      const selfMatch = n.numero.includes(q) || n.intitule.toLowerCase().includes(q);
      if (selfMatch || childMatches.length > 0) {
        return { ...n, children: childMatches };
      }
      return null;
    };
    return tree.map(match).filter((n): n is NodeWithChildren => n !== null);
  }, [tree, search]);

  // expand all on search
  useEffect(() => {
    if (search.trim()) {
      const all = new Set<string>();
      const walk = (n: NodeWithChildren): void => {
        all.add(n.id);
        n.children.forEach(walk);
      };
      tree.forEach(walk);
      setExpanded(all);
    }
  }, [search, tree]);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('parametres.plan.title')} subtitle={t('parametres.plan.subtitle')} />
      <div className="space-y-4 p-6">
        <div className="relative w-96 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('parametres.plan.search')}
            className="h-9 pl-8"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <ul className="text-sm">
              {filtered.map((node) => (
                <PlanNode
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggle}
                  canEdit={isAdmin}
                  onAddSub={(parent) => setDialog({ open: true, parent, editing: null })}
                  onEdit={(n) => setDialog({ open: true, parent: null, editing: n })}
                  onDelete={(n) => setConfirmDelete(n)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>

      <SubAccountDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        parent={dialog.parent}
        editing={dialog.editing}
        entreprises={entreprises}
        onSubmit={async (v) => {
          await upsert.mutateAsync(v as UpsertSubAccountInput);
          toast.success(dialog.editing ? 'Sous-compte mis à jour' : 'Sous-compte créé');
          setDialog({ open: false, parent: null, editing: null });
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer le sous-compte ${confirmDelete?.numero} — ${confirmDelete?.intitule} ?`}
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Sous-compte supprimé');
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

interface PlanNodeProps {
  node: NodeWithChildren;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  canEdit: boolean;
  onAddSub: (parent: PlanComptableRow) => void;
  onEdit: (n: PlanComptableRow) => void;
  onDelete: (n: PlanComptableRow) => void;
}

function PlanNode({
  node,
  depth,
  expanded,
  onToggle,
  canEdit,
  onAddSub,
  onEdit,
  onDelete,
}: PlanNodeProps): JSX.Element {
  const isOpen = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  return (
    <li className="border-b border-border last:border-0">
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30',
          depth === 0 && 'bg-muted/40 font-medium',
        )}
        style={{ paddingLeft: 12 + depth * 18 }}
      >
        <button
          type="button"
          onClick={() => hasChildren && onToggle(node.id)}
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded text-muted-foreground',
            hasChildren && 'hover:bg-muted',
          )}
          aria-label={isOpen ? 'Replier' : 'Déplier'}
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )
          ) : null}
        </button>
        <span className="font-mono text-xs tabular-nums">{node.numero}</span>
        <span className="flex-1 truncate">{node.intitule}</span>
        {node.is_system && (
          <Badge variant="outline" className="inline-flex items-center gap-1 text-[10px]">
            <Lock className="h-2.5 w-2.5" />
            {t('common.system')}
          </Badge>
        )}
        {canEdit && (
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onAddSub(node)}
              aria-label={t('parametres.plan.addSubAccount')}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            {!node.is_system && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive"
                onClick={() => onDelete(node)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
      {isOpen && hasChildren && (
        <ul>
          {node.children.map((c) => (
            <PlanNode
              key={c.id}
              node={c}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              canEdit={canEdit}
              onAddSub={onAddSub}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

const subSchema = z.object({
  numero: z.string().min(1).max(20),
  intitule: z.string().min(2),
  classe: z.coerce.number().int().min(1).max(7),
  entreprise_id: z.string().uuid(),
  description: z.string().optional().nullable(),
});
type SubForm = z.infer<typeof subSchema>;

function SubAccountDialog({
  open,
  onOpenChange,
  parent,
  editing,
  entreprises,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  parent: PlanComptableRow | null;
  editing: PlanComptableRow | null;
  entreprises: { id: string; nom: string }[];
  onSubmit: (v: SubForm & { parent_id: string; id?: string }) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SubForm>({
    resolver: zodResolver(subSchema),
    defaultValues: { numero: '', intitule: '', classe: 1, entreprise_id: '' },
  });

  useEffect(() => {
    if (open) {
      const baseClasse = parent?.classe ?? editing?.classe ?? 1;
      reset({
        numero: editing?.numero ?? (parent ? `${parent.numero}` : ''),
        intitule: editing?.intitule ?? '',
        classe: baseClasse,
        entreprise_id: editing?.entreprise_id ?? entreprises[0]?.id ?? '',
        description: editing?.description ?? '',
      });
    }
  }, [open, editing, parent, reset, entreprises]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('parametres.plan.addSubAccount')}
      description={
        parent ? `Sous-compte de ${parent.numero} — ${parent.intitule}` : 'Sous-compte personnalisé'
      }
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        if (!parent && !editing) return;
        await onSubmit({
          ...v,
          parent_id: editing?.parent_id ?? parent?.id ?? '',
          id: editing?.id,
        });
      })}
    >
      <FormSection>
        <FormField
          label="Numéro"
          required
          {...register('numero')}
          hint="ex. 4111, 6411…"
          error={errors.numero?.message}
        />
        <FormField type="number" label="Classe (1-7)" required {...register('classe')} />
        <FormField
          label="Intitulé"
          required
          {...register('intitule')}
          error={errors.intitule?.message}
          fullWidth
        />
        <FormSelect
          label="Entreprise"
          required
          value={watch('entreprise_id')}
          onChange={(v) => setValue('entreprise_id', v, { shouldValidate: true })}
          options={entreprises.map((e) => ({ value: e.id, label: e.nom }))}
          fullWidth
        />
        <FormTextarea label="Description (optionnelle)" {...register('description')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}
